import type { AppSettings } from "@nmt/shared";
import { useEffect, useState } from "react";
import { errorMessage } from "../../api";
import { AlertPicker, alertsText } from "../../components/alert-picker";
import { Alert, Button, Loading, PageHeader, Panel, PanelHeader, SelectField } from "../../components/ui";
import { useSettings } from "./useSettings";

type Form = Pick<AppSettings, "calendar.alertsTimed" | "calendar.alertsAllDay" | "calendar.alertsByType" | "tasks.dueReminderAt" | "tasks.dueReminderMinutes">;

const DUE_TIMES = ["06:00", "07:00", "08:00", "09:00", "10:00", "12:00", "17:00"];
const DUE_BEFORE = [15, 30, 60, 120, 240, 1440];
const timeLabel = (t: string) => {
  const [h, m] = t.split(":").map(Number) as [number, number];
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "AM" : "PM"}`;
};

/**
 * Settings → Calendar and reminders: when appointments remind people (on their Google Calendar, and
 * from the CRM for those who want "Appointment reminders"), per type, and when tasks coming due do.
 */
export function CalendarSettings() {
  const { query, save } = useSettings();
  const [form, setForm] = useState<Form | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const s = query.data?.settings;
    if (s && !form) setForm({ "calendar.alertsTimed": s["calendar.alertsTimed"], "calendar.alertsAllDay": s["calendar.alertsAllDay"], "calendar.alertsByType": s["calendar.alertsByType"], "tasks.dueReminderAt": s["tasks.dueReminderAt"], "tasks.dueReminderMinutes": s["tasks.dueReminderMinutes"] });
  }, [query.data, form]);
  if (query.isLoading || !form || !query.data) return <Loading />;
  const types = query.data.settings["lists.appointmentTypes"];
  const set = (patch: Partial<Form>) => {
    setNotice(null);
    setForm({ ...form, ...patch });
  };
  const setType = (t: string, v: number[] | "google" | null) => {
    const next = { ...form["calendar.alertsByType"] };
    if (v === null) delete next[t];
    else next[t] = v;
    set({ "calendar.alertsByType": next });
  };
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Calendar and reminders"
        description="When appointments and tasks remind people. Appointment alerts go on everyone's Google Calendar (for those who connected it) and come from the CRM as push, text, or email for people who want Appointment reminders (My profile → Notifications)."
      />
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form, { onSuccess: () => setNotice("Saved. Upcoming appointments on Google Calendars update in the next few minutes.") });
        }}
      >
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        <Panel>
          <PanelHeader title="Appointment alerts" description="The company's times, used unless an appointment's type or the appointment itself has its own." />
          <div className="flex flex-col gap-4 px-5 py-4 text-sm">
            <div>
              <div className="mb-1.5 font-semibold">Appointments with a time</div>
              <AlertPicker value={form["calendar.alertsTimed"]} onChange={(v) => set({ "calendar.alertsTimed": v })} allDay={false} />
            </div>
            <div>
              <div className="mb-1.5 font-semibold">All-day work (installs, deliveries)</div>
              <AlertPicker value={form["calendar.alertsAllDay"]} onChange={(v) => set({ "calendar.alertsAllDay": v })} allDay />
            </div>
          </div>
        </Panel>
        <Panel>
          <PanelHeader title="By appointment type" description="A type can have its own times, or leave Google Calendar to each person's own alert settings (the CRM's reminders then use the company's times)." />
          <ul className="px-5 py-2 text-sm">
            {types.map((t) => {
              const v = form["calendar.alertsByType"][t];
              const mode = v === undefined ? "company" : v === "google" ? "google" : "own";
              return (
                <li key={t} className="flex flex-col gap-2 border-b border-mist py-3 last:border-0">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="font-semibold">{t}</span>
                    <SelectField label={`Alerts for ${t}`} className="w-64 [&>label]:sr-only" value={mode} onChange={(e) => setType(t, e.target.value === "company" ? null : e.target.value === "google" ? "google" : [...form["calendar.alertsTimed"]])}>
                      <option value="company">The company's ({alertsText(form["calendar.alertsTimed"], false)})</option>
                      <option value="own">Its own times</option>
                      <option value="google">Each person's Google defaults</option>
                    </SelectField>
                  </div>
                  {Array.isArray(v) && <AlertPicker value={v} onChange={(x) => setType(t, x)} allDay={t === "Install" || t === "Material delivery"} />}
                </li>
              );
            })}
          </ul>
        </Panel>
        <Panel>
          <PanelHeader title="Task due reminders" description="For a task with a due date and no reminder of its own. Goes to the people on it, as their Task reminders (push, text, or email)." />
          <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
            <SelectField label="Due on a day (no time)" value={form["tasks.dueReminderAt"] ?? ""} onChange={(e) => set({ "tasks.dueReminderAt": e.target.value || null })}>
              <option value="">No reminder</option>
              {DUE_TIMES.map((t) => (
                <option key={t} value={t}>
                  {timeLabel(t)} that day
                </option>
              ))}
            </SelectField>
            <SelectField label="Due at a time" value={form["tasks.dueReminderMinutes"] === null ? "" : String(form["tasks.dueReminderMinutes"])} onChange={(e) => set({ "tasks.dueReminderMinutes": e.target.value === "" ? null : Number(e.target.value) })}>
              <option value="">No reminder</option>
              {DUE_BEFORE.map((m) => (
                <option key={m} value={m}>
                  {m >= 1440 ? "1 day before" : m >= 60 ? `${m / 60} hour${m > 60 ? "s" : ""} before` : `${m} minutes before`}
                </option>
              ))}
            </SelectField>
          </div>
        </Panel>
        <div className="flex justify-end">
          <Button variant="primary" type="submit" busy={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </div>
  );
}
